/**
 * useAgentSSE — DeepSeek 스타일 실시간 스트리밍 에이전트 채팅 훅
 *
 * 5개 사이트 공통 SSE 클라이언트.
 * POST /api/store/assistant/stream → text/event-stream 수신.
 *
 * 상태 머신: idle → streaming → complete
 *                               └→ error
 *
 * 사용법:
 *   const { messages, sendMessage, status, error } = useAgentSSE();
 */
import { useState, useCallback, useRef } from "react";

/**
 * @param {Object} options
 * @param {string} [options.endpoint="/api/store/assistant/stream"]
 * @param {function} [options.getHeaders] - 추가 헤더 (인증 등)
 * @param {function} [options.onEvent] - 이벤트 콜백 (디버깅)
 */
export default function useAgentSSE({
  endpoint = "/api/store/assistant/stream",
  getHeaders = () => ({}),
  onEvent = null,
} = {}) {
  const [messages, setMessages] = useState([]);
  const [status, setStatus] = useState("idle"); // idle | streaming | complete | error
  const [error, setError] = useState(null);
  const [currentThoughts, setCurrentThoughts] = useState([]);
  const [currentTools, setCurrentTools] = useState([]);
  const [streamingText, setStreamingText] = useState("");
  const sessionIdRef = useRef(null);
  const abortRef = useRef(null);

  const sendMessage = useCallback(
    async (message) => {
      if (!message.trim() || status === "streaming") return;

      // UI에 사용자 메시지 추가
      setMessages((prev) => [...prev, { role: "user", content: message }]);
      setStatus("streaming");
      setError(null);
      setCurrentThoughts([]);
      setCurrentTools([]);
      setStreamingText("");

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const headers = {
          "Content-Type": "application/json",
          ...getHeaders(),
        };

        const resp = await fetch(endpoint, {
          method: "POST",
          headers,
          body: JSON.stringify({
            message,
            history: messages.slice(-6).map(({ role, content }) => ({ role, content })),
            session_id: sessionIdRef.current || "",
          }),
          signal: controller.signal,
        });

        if (!resp.ok) {
          const errData = await resp.json().catch(() => ({}));
          throw new Error(errData.error || `HTTP ${resp.status}`);
        }

        const reader = resp.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let accumulatedAnswer = "";
        const thoughts = [];
        const tools = [];

        while (true) {
          const { value, done } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() || "";

          for (const line of lines) {
            if (!line.startsWith("data: ")) continue;
            const payload = line.slice(6);
            if (payload === "[DONE]") continue;

            let event;
            try {
              event = JSON.parse(payload);
            } catch {
              continue;
            }

            if (onEvent) onEvent(event);

            switch (event.type) {
              case "thought":
                thoughts.push(event.message);
                setCurrentThoughts([...thoughts]);
                break;

              case "tool_call":
                tools.push({
                  name: event.tool_name,
                  args: event.args || {},
                  status: "running",
                  result: null,
                });
                setCurrentTools([...tools]);
                break;

              case "tool_result": {
                const idx = tools.findIndex(
                  (t) => t.name === event.tool_name && t.status === "running"
                );
                if (idx >= 0) {
                  tools[idx] = { ...tools[idx], status: "done", result: event.result };
                  setCurrentTools([...tools]);
                }
                break;
              }

              case "final_answer":
                accumulatedAnswer += event.content || "";
                setStreamingText(accumulatedAnswer);
                break;

              case "error":
                setError(event.message);
                break;

              case "session":
                sessionIdRef.current = event.session_id;
                break;

              default:
                break;
            }
          }
        }

        // 스트리밍 완료 → 메시지에 추가
        if (accumulatedAnswer) {
          setMessages((prev) => [
            ...prev,
            {
              role: "assistant",
              content: accumulatedAnswer,
              thoughts,
              tools,
            },
          ]);
        }

        setStreamingText("");
        setCurrentThoughts([]);
        setCurrentTools([]);
        setStatus("complete");
      } catch (err) {
        if (err.name === "AbortError") {
          setStatus("idle");
        } else {
          setError(err.message);
          setStatus("error");
          setMessages((prev) => [
            ...prev,
            { role: "assistant", content: `오류: ${err.message}`, isError: true },
          ]);
        }
      }
    },
    [endpoint, getHeaders, onEvent, status, messages]
  );

  const stop = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
  }, []);

  const reset = useCallback(() => {
    setMessages([]);
    setStatus("idle");
    setError(null);
    setStreamingText("");
    setCurrentThoughts([]);
    setCurrentTools([]);
    sessionIdRef.current = null;
  }, []);

  return {
    messages,
    sendMessage,
    stop,
    reset,
    status,
    error,
    streamingText,
    currentThoughts,
    currentTools,
    sessionId: sessionIdRef.current,
  };
}
